/**
 * Camlin Companion — host page theme detection (Keith, round 3,
 * 6 Aug 2026: "sync the panel with the host site's light/dark theme …
 * detect prefers-color-scheme and/or the page's own theme toggle").
 *
 * Resolution order (most explicit wins):
 *   1. An explicit theme marker on <html> or <body>: class "dark"/"light"
 *      or a data-theme attribute containing dark/light — this is what
 *      page-level theme toggles (Tailwind's `dark` class, `data-theme`)
 *      actually flip, so honouring it keeps the panel in lockstep with
 *      the page's own switch, not just the OS setting.
 *   2. The page's real background: computed body/html background-color
 *      luminance (covers sites with a fixed palette and no marker).
 *   3. prefers-color-scheme.
 *
 * `watchHostTheme` re-resolves on html/body attribute mutations and on
 * prefers-color-scheme changes, so a mid-session toggle restyles the
 * panel live.
 */

export type CompanionHostTheme = 'light' | 'dark';

function markerTheme(el: Element | null): CompanionHostTheme | null {
  if (!el) return null;
  const className = typeof el.className === 'string' ? el.className : '';
  const dataTheme = el.getAttribute('data-theme') ?? '';
  const haystack = `${className} ${dataTheme}`.toLowerCase();
  if (/\bdark\b/.test(haystack)) return 'dark';
  if (/\blight\b/.test(haystack)) return 'light';
  return null;
}

/** Parse `rgb(a)` computed color into luminance 0..1, null if transparent/unparseable. */
function luminanceOf(color: string): number | null {
  const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/);
  if (!match) return null;
  const alpha = match[4] === undefined ? 1 : parseFloat(match[4]);
  if (alpha < 0.1) return null;
  const [r, g, b] = [Number(match[1]), Number(match[2]), Number(match[3])];
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

export function resolveHostTheme(): CompanionHostTheme {
  if (typeof document === 'undefined') {
    return 'light';
  }

  const explicit = markerTheme(document.documentElement) ?? markerTheme(document.body);
  if (explicit) {
    return explicit;
  }

  try {
    const bodyLum = luminanceOf(getComputedStyle(document.body).backgroundColor)
      ?? luminanceOf(getComputedStyle(document.documentElement).backgroundColor);
    if (bodyLum !== null) {
      return bodyLum < 0.45 ? 'dark' : 'light';
    }
  } catch {
    // getComputedStyle can throw in detached/test contexts — fall through.
  }

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  return 'light';
}

export function watchHostTheme(onChange: (theme: CompanionHostTheme) => void): () => void {
  if (typeof document === 'undefined') {
    return () => {};
  }

  let current = resolveHostTheme();
  const reevaluate = () => {
    const next = resolveHostTheme();
    if (next !== current) {
      current = next;
      onChange(next);
    }
  };

  const observer = new MutationObserver(reevaluate);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme', 'style'] });
  if (document.body) {
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme', 'style'] });
  }

  let mql: MediaQueryList | null = null;
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    mql = window.matchMedia('(prefers-color-scheme: dark)');
    mql.addEventListener('change', reevaluate);
  }

  return () => {
    observer.disconnect();
    mql?.removeEventListener('change', reevaluate);
  };
}
